import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormatListBulletedOutlined, Person } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	LoadingState,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import { getApiErrorMessage, useDeleteTestimonial, useGetTestimonialById } from '@practium/features/api';
import type { FormPageMode } from '@practium/ui';
import { TestimonialForm } from '../components/Forms/TestimonialForm/TestimonialForm';
import type { TestimonialFormRef } from '../components/Forms/TestimonialForm/TestimonialForm';
import { TestimonialPreviewCard } from '../components/Preview/TestimonialPreviewCard/TestimonialPreviewCard';
import { AvatarCard } from '@practium/features/users';
import { usePermissions } from '@practium/features/permissions';
import type { TestimonialFormData } from '../types/landing.types';
import { LANDING_ROUTES } from '../constants/landing.constants';
import styles from './AddTestimonialPage.module.css';

export const ViewTestimonialPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { abilities } = usePermissions();
	const canEdit = abilities.can('update', 'testimonials');
	const canDelete = abilities.can('delete', 'testimonials');
	const id = searchParams.get('id') ?? '';
	const { data: testimonial, isLoading: isLoadingTestimonial } = useGetTestimonialById(id);
	const { mutateAsync: deleteTestimonial, isPending: isDeleting } = useDeleteTestimonial();

	const avatarUrl = testimonial?.avatarPath?.url;

	const initialData: TestimonialFormData = useMemo(
		() => ({
			name: testimonial?.name ?? '',
			occupation: testimonial?.occupation ?? '',
			testimonial: testimonial?.testimonial ?? '',
			status: testimonial?.status ? 'yes' : 'no',
		}),
		[testimonial]
	);

	const [currentMode, setCurrentMode] = useState<FormPageMode>('view');
	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [liveForm, setLiveForm] = useState<TestimonialFormData>(initialData);

	const formRef = useRef<TestimonialFormRef>(null);

	const { success: toastSuccess, error: toastError } = useToast();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const handleSubmit = useCallback(() => {
		navigate(LANDING_ROUTES.CONTROL);
	}, [navigate]);

	useEffect(() => {
		if (currentMode === 'view') setLiveForm(initialData);
	}, [currentMode, initialData]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setCurrentMode('view');
			setLiveForm(initialData);
			setAvatarFile(null);
		};
		if (!avatarFile && !formRef.current?.isDirty?.()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [avatarFile, confirm, initialData]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir depoimento',
			message: 'Tem certeza que deseja excluir este depoimento? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await deleteTestimonial(id);
			toastSuccess('Depoimento excluído com sucesso');
			navigate(LANDING_ROUTES.CONTROL);
		} catch (error) {
			toastError(getApiErrorMessage(error, 'Erro ao excluir depoimento'));
		}
	}, [confirm, deleteTestimonial, id, navigate, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);
	const handleSave = useCallback(() => formRef.current?.submit(), []);

	const pageTitle =
		currentMode === 'view' ? 'Informações do Depoimento' : 'Editar Depoimento';
	const pageSubtitle =
		currentMode === 'view'
			? 'Visualize os dados do depoimento'
			: 'Edite os dados do depoimento';

	if (isLoadingTestimonial) return <LoadingState fullScreen />;

	return (
		<div className={styles.page}>
			<PageHeader
				title={pageTitle}
				subtitle={pageSubtitle}
				onBack={() => navigate(LANDING_ROUTES.CONTROL)}
			/>
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Gerais"
						className={styles.mainCard}
					>
						<TestimonialForm
							ref={formRef}
							mode={currentMode}
							initialData={initialData}
							testimonialId={id}
							avatarFile={avatarFile}
							onSubmit={handleSubmit}
							onLoadingChange={setIsLoading}
							onFormChange={setLiveForm}
						/>
					</FormCard>
					<div className={styles.pageActions}>
						<FormActions
							mode={currentMode}
							onCancel={handleCancel}
							onSave={handleSave}
							isLoading={isLoading || isDeleting}
							onDelete={canDelete ? handleDelete : undefined}
							onEdit={canEdit ? handleEdit : undefined}
						/>
					</div>
				</div>

			</div>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
